import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";

import BackofficeLayout from "@/components/layouts/BackofficeLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  MoreHorizontal, 
  Pencil, 
  Plus, 
  Trash,
  Loader2,
  Search,
  X
} from "lucide-react";
import { pricingTableApi } from "@/services/api";
import { PricingTableSearchParams } from "@/models";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const Pricing = () => {
  const [searchParams, setSearchParams] = useState<PricingTableSearchParams>({
    search: "",
    limit: 300,
    offset: 0
  });
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [searchValue, setSearchValue] = useState<string>("");
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { userRoles, selectedRole, loading, permissionsLoaded, hasPermission, getCurrentRoleIds } = useAuth();
  
  // Check if user can create pricing tables
  const canCreatePricingTable = () => {
    // Platform users cannot create pricing tables
    if (selectedRole?.type === 'platform') {
      return false;
    }
    
    // Management company users need write permission for pricing_tables
    if (selectedRole?.type === 'management_company') {
      const currentRoleIds = getCurrentRoleIds();
      return hasPermission(currentRoleIds, 'write', 'pricing_tables');
    }
    
    return false;
  };

  // Check if user can write/edit pricing tables
  const canWritePricingTables = (() => {
    if (!selectedRole || !permissionsLoaded) return false;
    
    if (selectedRole.type === 'platform') {
      return hasPermission(getCurrentRoleIds(), 'write', 'pricing_tables');
    } else if (selectedRole.type === 'management_company') {
      return hasPermission(getCurrentRoleIds(), 'write', 'pricing_tables');
    }
    return false;
  })();
  
  // Debounce search to prevent excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchValue !== searchParams.search) {
        setSearchParams({ ...searchParams, search: searchValue });
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchValue]);

  // Get API filters based on selected role - simplified for backend filtering
  const getApiFilters = () => {
    const filters: any = {};
    // Backend will handle role-based filtering automatically
    // No need to pass show_all, show_none, or management_company_id
    return filters;
  };
  
  const { 
    data: pricing, 
    isLoading, 
    error,
    isFetching
  } = useQuery({
    queryKey: ['pricing-tables', selectedRole?.type, selectedRole?.entityId, searchParams.search],
    queryFn: async () => {
      const filters = getApiFilters();
      const result = await pricingTableApi.list({ 
        search: searchParams.search,
        ...filters
      });
      // Ensure result is always an array and each item has document_fees
      return Array.isArray(result) ? result.map(item => ({
        ...item,
        document_fees: item.document_fees || []
      })) : [];
    },
    enabled: permissionsLoaded && !!selectedRole,
    meta: {
      onError: (err: Error) => {
        console.error('Pricing tables query error:', err);
        toast({
          title: "Error loading pricing tables",
          description: err.message,
          variant: "destructive"
        });
      }
    }
  });
  
  const deleteMutation = useMutation({
    mutationFn: (id: number) => pricingTableApi.delete(id),
    onSuccess: (_, deletedId) => {
      toast({
        title: "Pricing table deleted",
        description: "The pricing table has been deleted successfully."
      });
      
      queryClient.setQueryData(
        ["pricing-tables", searchParams], 
        (oldData: any) => oldData.filter((table: any) => table.id !== deletedId)
      );
      
      setDeletingId(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Error deleting pricing table",
        description: error.message,
        variant: "destructive"
      });
    }
  });
  
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };
  
  const clearSearch = () => {
    setSearchValue("");
    setSearchParams({ ...searchParams, search: "" });
  };
  
  const handleDelete = (id: number) => {
    deleteMutation.mutate(id);
  };

  const handleRowClick = (pricingTableId: number, event: React.MouseEvent) => {
    if ((event.target as HTMLElement).closest('[data-actions-menu]')) {
      return;
    }
    navigate(`/backoffice/pricing/${pricingTableId}`);
  };

  // Show loading state while permissions are loading
  if (loading || !permissionsLoaded) {
    return (
      <BackofficeLayout>
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-brand-blue" />
          <span className="ml-2">Loading permissions...</span>
        </div>
      </BackofficeLayout>
    );
  }

  // Show message if no role is selected
  if (!selectedRole) {
    return (
      <BackofficeLayout>
        <div className="text-center py-8">
          <h3 className="text-lg font-medium">No role selected</h3>
          <p className="text-sm text-muted-foreground">
            Please select a role to view pricing tables.
          </p>
        </div>
      </BackofficeLayout>
    );
  }
  
  if (isLoading) {
    return (
      <BackofficeLayout>
        <div className="flex justify-center items-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-brand-blue" />
        </div>
      </BackofficeLayout>
    );
  }
  
  if (error) {
    return (
      <BackofficeLayout>
        <div className="flex justify-center items-center min-h-[60vh] text-red-500">
          Error: {error.message}
        </div>
      </BackofficeLayout>
    );
  }

  return (
    <BackofficeLayout>
      <div className="container mx-auto py-10">
        <div className="flex flex-col md:flex-row justify-between items-center mb-6">
          <div className="text-2xl font-bold">Pricing Tables</div>
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <div className="relative w-full md:w-auto">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search pricing tables..."
                className="pl-8 pr-10 max-w-md md:w-auto"
                value={searchValue}
                onChange={handleSearchChange}
              />
              {searchValue && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="absolute right-0 top-0 h-full aspect-square rounded-l-none" 
                  onClick={clearSearch}
                >
                  <X className="h-4 w-4" />
                  <span className="sr-only">Clear search</span>
                </Button>
              )}
            </div>
            {canCreatePricingTable() && (
              <Button asChild>
                <Link to="/backoffice/pricing/new">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Pricing Table
                </Link>
              </Button>
            )}
          </div>
        </div>

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Document Types</TableHead>
                <TableHead>Created At</TableHead>
                {canWritePricingTables && <TableHead className="text-right"></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isFetching ? (
                <TableRow>
                  <TableCell colSpan={canWritePricingTables ? 4 : 3} className="h-24 text-center">
                    <Loader2 className="h-5 w-5 mx-auto animate-spin text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : pricing && pricing.length > 0 ? (
                pricing.map((pricingTable) => (
                  <TableRow 
                    key={pricingTable.id} 
                    className={canWritePricingTables ? "cursor-pointer" : ""}
                    onClick={canWritePricingTables ? (e) => handleRowClick(pricingTable.id, e) : undefined}
                  >
                    <TableCell>
                      <div className="font-medium">{pricingTable.name}</div>
                      <div className="text-sm text-muted-foreground truncate max-w-[200px]">
                        {pricingTable.description || 'No description'}
                      </div>
                    </TableCell>
                    <TableCell>
                      {pricingTable.document_fees?.length || 0} document types
                    </TableCell>
                    <TableCell>
                      {format(new Date(pricingTable.created_at), 'MMM dd, yyyy')}
                    </TableCell>
                    {canWritePricingTables && (
                      <TableCell data-actions-menu>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" onClick={(e) => e.stopPropagation()}>
                              <MoreHorizontal className="h-4 w-4" />
                              <span className="sr-only">Open menu</span>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                            <DropdownMenuItem asChild>
                              <Link to={`/backoffice/pricing/${pricingTable.id}`}>
                                <Pencil className="mr-2 h-4 w-4" />
                                Edit
                              </Link>
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => setDeletingId(pricingTable.id)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash className="mr-2 h-4 w-4" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={canWritePricingTables ? 4 : 3} className="h-24 text-center">
                    {searchParams.search ? (
                      <div>
                        <p className="text-muted-foreground">No pricing tables found for "{searchParams.search}"</p>
                        <Button variant="link" onClick={clearSearch} className="mt-2">
                          Clear search
                        </Button>
                      </div>
                    ) : (
                      <p className="text-muted-foreground">
                        {isFetching ? "Loading pricing tables..." : "No pricing tables available"}
                      </p>
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
      
      <Dialog open={deletingId !== null} onOpenChange={() => setDeletingId(null)}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Are you sure absolutely sure?</DialogTitle>
            <DialogDescription>
              This action cannot be undone. This will permanently delete the pricing table and remove its data from our servers.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => setDeletingId(null)} disabled={deleteMutation.isPending}>
              Cancel
            </Button>
            <Button 
              type="button" 
              variant="destructive" 
              onClick={() => {
                if (deletingId !== null) {
                  handleDelete(deletingId);
                }
              }} 
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <div className="flex items-center">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </div>
              ) : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </BackofficeLayout>
  );
};

export default Pricing;
